import * as React from "react";
import dayjs from "dayjs";
import { toISODate } from "../utils/dateUtils";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DayCalendarSkeleton } from "@mui/x-date-pickers/DayCalendarSkeleton";
import CalendarServices from "../services/v1/calendarServices";

function ServerDay(props) {
  const {
    highlightedDays = [],
    day,
    outsideCurrentMonth,
    selected,
    ...other
  } = props;

  const isPast = day.isBefore(dayjs(), "day");
  const dayString = toISODate(day.format("YYYY-MM-DD"));
  const dayData = highlightedDays.find((d) => d.date === dayString);
  const isReserved = !outsideCurrentMonth && dayData?.type === "reserved";
  const isBlocked = !outsideCurrentMonth && dayData?.type === "blocked";
  const tooltipTitle = isReserved ? "Date Already reserved" : isBlocked ? "Date Unavailable" : "";

  const dayNode = (
    <Badge key={day.toString()} overlap="circular">
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
        disabled={isPast || isReserved || isBlocked}
        selected={selected}
        sx={{
          backgroundColor: isBlocked ? "#D3D3D3" : isReserved ? "#d6a3bb" : "#fff",
          "&.Mui-selected": {
            backgroundColor: "#8B2757",
            "&:hover": {
              backgroundColor: "#7a2049",
            },
            "&:focus": {
              backgroundColor: "#7a2049",
            },
          },
        }}
      />
    </Badge>
  );

  if (tooltipTitle) {
    return (
      <Tooltip
        title={tooltipTitle}
        placement="top"
        arrow
        enterTouchDelay={0}
        leaveTouchDelay={2000}
      >
        <span>{dayNode}</span>
      </Tooltip>
    );
  }

  return dayNode;
}

export default function DateCalendarServerRequest({ onDateChange, value, excludeDate }) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [highlightedDays, setHighlightedDays] = React.useState([]);

  // excludeDate: a reservation's own current date shouldn't render as
  // "already reserved" when that same reservation is the one being edited.
  const excludeISO = excludeDate ? toISODate(dayjs(excludeDate).format("YYYY-MM-DD")) : null;

  const fetchReservedDates = async (date) => {
    setIsLoading(true);
    try {
      const response = await CalendarServices.allReservedDates();
      const reservedDates = response.data || [];
      setHighlightedDays(
        reservedDates
          .map((d) => ({
            date: toISODate(d.date),
            type: d.type, // "reserved" or "blocked"
          }))
          .filter((d) => d.date !== excludeISO),
      );
    } catch (error) {
      console.error("Failed to fetch reserved dates:", error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchReservedDates(dayjs());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeISO]);

  const handleMonthChange = (date) => {
    fetchReservedDates(date);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateCalendar
        value={value ? dayjs(value) : null}
        loading={isLoading}
        onMonthChange={handleMonthChange}
        onChange={(date) => onDateChange(date)}
        renderLoading={() => <DayCalendarSkeleton />}
        slots={{
          day: ServerDay,
        }}
        slotProps={{
          day: {
            highlightedDays,
          },
        }}
      />
    </LocalizationProvider>
  );
}
